<template>
  <div>
    <div class="word-card" v-if="similarity">
      <div class="word-title">{{ similarity.word }}发音解决方案四</div>

      <div class="info-panel syllabary-panel">
        <span class="panel-label">🔗 温馨提示</span>
        <span class="panel-text">✂️点击相邻的独立音标，可以试听他们的组合发音，点击字母或者字母组合可以查询字母以及发音一样的单词。</span>
      </div>

      <!-- 音标 / 字母 分行展示 -->
      <div class="info-panel letter-panel" v-if="individualLettersArr.length">
        <div class="row-label label-pho">🎵 独立音标</div>
        <div class="chip-row">
          <span
            v-for="(phone, i) in individualPhoneticsArr"
            :key="'p' + i"
            class="chip chip-pho"
            :class="{ 'chip-active': selectedIdxs[i] }"
          >/{{ phone }}/</span>
        </div>

        <div class="row-label label-let">🔤 对应字母</div>
        <div class="chip-row">
          <span
            v-for="(letter, i) in individualLettersArr"
            :key="'l' + i"
            class="chip chip-let"
            :class="{ 'chip-selected': selectedIdxs[i] }"
            @click="searchForSelectedSimilarity(i)"
          >{{ letter }}</span>
        </div>

        <!-- 已选组合 -->
        <div class="selected-box" v-if="selectedChips.length">
          <div class="selected-label">已选组合（默认第一个音素，点击字母可以重选）：</div>
          <div class="combo-row">
            <span class="combo-label">字母：</span>
            <template v-for="(c, i) in selectedChips">
              <span v-if="i > 0" :key="'s' + i" class="combo-sep">=</span>
              <span
                :key="'c' + i"
                class="combo-letter"
                :class="{ 'combo-removable': i === 0 || i === selectedChips.length - 1, 'combo-locked': i !== 0 && i !== selectedChips.length - 1 }"
                @click="removeSelectedChip(c.idx)"
              >{{ c.letter }}</span>
            </template>
          </div>
          <div class="combo-row">
            <span class="combo-label">音标：</span>
            <template v-for="(c, i) in selectedChips">
              <span v-if="i > 0" :key="'ss' + i" class="combo-sep">=</span>
              <span :key="'cc' + i" class="combo-pho">/{{ c.phonetic }}/</span>
            </template>
          </div>
          <div class="combo-actions">
            <button class="combo-btn combo-search" @click="searchByCombo">🔍 搜索</button>
            <button class="combo-btn combo-clear" @click="clearAllSelected">✕ 清空选择</button>
          </div>
        </div>
      </div>

      <!-- 搜索结果 -->
      <div class="info-panel result-panel" v-if="similarResultArr.length">
        <span class="panel-label">🎯 相似单词</span>
        <div class="similar-chips">
          <button
            v-for="(item, i) in similarResultArr"
            :key="i"
            class="similar-btn"
            @click="showWordDetail(i)"
          >
            <span v-for="(c, k) in item.chars" :key="k" class="btn-letter" :class="{ 'btn-letter-hl': c.highlight }">{{ c.ch }}</span>
          </button>
        </div>
      </div>

      <button class="back-btn" @click="$emit('back')">返回单词详解</button>
    </div>

    <div class="word-card empty-card" v-else>
      <span class="empty-text">{{ errorMessage || '暂无相似词信息' }}</span>
    </div>

    <!-- 浮动详情 -->
    <div class="float-tip" v-if="floatingData" @click="clearFloating">
      <div class="float-word">{{ floatingData.word }}</div>
      <div class="float-meaning">{{ floatingData.meaning }}</div>
      <div class="float-meta" v-if="floatingData.grade">
        <span class="float-meta-item">📚 年级：{{ floatingData.grade }}</span>
        <span class="float-meta-item">📖 学期：{{ floatingData.term }}</span>
        <span class="float-meta-item">📗 单元：{{ floatingData.unit }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import request from '../../utils/request'
import { depart, normalizeNewlines, markWordChars } from '../../utils/similar'

export default {
  name: 'FreeSelectPhonetic',
  data() {
    return {
      similarity: null,
      errorMessage: '',
      individualLettersArr: [],
      individualPhoneticsArr: [],
      selectedChips: [],
      selectedIdxs: {},
      similarResultArr: [],
      floatingData: null
    }
  },
  computed: {
    word() {
      return this.$store.state.word
    }
  },
  watch: {
    word(val) {
      if (val && val.trim()) this.fetchSimilarity(val)
    }
  },
  mounted() {
    const word = (this.$store.state.word || '').trim()
    if (word) this.fetchSimilarity(word)
  },
  methods: {
    async fetchSimilarity(word) {
      if (this._fetchingWord === word) return
      this._fetchingWord = word
      try {
        const token = localStorage.getItem('satoken') || ''
        const res = await request.get('/newSimilarity/searchForSimilarity', {
          params: { word, code: token }
        })
        if (res.code === 200) {
          const info = res.NewSimilarity
          if (!info) {
            this.similarity = null
            this.errorMessage = '暂无单词数据'
            return
          }
          const keys = ['individualPhonetics', 'individualLetters']
          keys.forEach((k) => {
            if (typeof info[k] === 'string') info[k] = normalizeNewlines(info[k])
          })

          this.similarity = info
          this.individualLettersArr = depart(info.individualLetters)
          this.individualPhoneticsArr = depart(info.individualPhonetics)
          this.errorMessage = ''
          this.similarResultArr = []

          if (this.individualLettersArr.length) {
            this.selectedChips = [{ idx: 0, letter: this.individualLettersArr[0], phonetic: this.individualPhoneticsArr[0] || '' }]
            this.selectedIdxs = { 0: true }
          } else {
            this.selectedChips = []
            this.selectedIdxs = {}
          }
        }
      } catch (e) {
        this.errorMessage = '查询失败，请重新登录！'
        this.similarity = null
      } finally {
        this._fetchingWord = null
      }
    },

    // 点击字母：首次任意选，之后只能选相邻；已选的只能取消两端
    searchForSelectedSimilarity(idx) {
      const letter = this.individualLettersArr[idx]
      const phonetic = this.individualPhoneticsArr[idx] || ''
      const { selectedChips, selectedIdxs } = this

      if (selectedIdxs[idx]) {
        const arrPos = selectedChips.findIndex((c) => c.idx === idx)
        if (arrPos !== 0 && arrPos !== selectedChips.length - 1) {
          this.$message.info('只能取消两端的字母')
          return
        }
        this.selectedChips = selectedChips.filter((c) => c.idx !== idx)
        this.syncSelectedIdxs()
        return
      }

      if (selectedChips.length === 0) {
        this.selectedChips = [{ idx, letter, phonetic }]
        this.syncSelectedIdxs()
        return
      }

      const isAdjacent = selectedChips.some((c) => Math.abs(c.idx - idx) === 1)
      if (!isAdjacent) {
        this.$message.info('只能选择相邻的字母')
        return
      }

      this.selectedChips = [...selectedChips, { idx, letter, phonetic }].sort((a, b) => a.idx - b.idx)
      this.syncSelectedIdxs()
    },

    removeSelectedChip(idx) {
      const { selectedChips } = this
      const arrPos = selectedChips.findIndex((c) => c.idx === idx)
      if (arrPos === -1) return
      if (arrPos !== 0 && arrPos !== selectedChips.length - 1) {
        this.$message.info('只能取消两端的字母')
        return
      }
      this.selectedChips = selectedChips.filter((c) => c.idx !== idx)
      this.syncSelectedIdxs()
    },

    clearAllSelected() {
      this.selectedChips = []
      this.selectedIdxs = {}
    },

    syncSelectedIdxs() {
      const map = {}
      this.selectedChips.forEach((c) => { map[c.idx] = true })
      this.selectedIdxs = map
    },

    searchByCombo() {
      if (this.selectedChips.length <= 1) {
        this.$message.info('请选择至少两个部分字母或者字母组合')
        return
      }
      const letterCombo = this.selectedChips.map((c) => c.letter).join('=')
      const phoneticCombo = this.selectedChips.map((c) => (c.phonetic || '').replace(/\//g, '')).join('=')
      this.fetchSimilarWord(this.$store.state.word || '', letterCombo, phoneticCombo)
    },

    async fetchSimilarWord(word, letter, phone) {
      if (!phone && !letter) return
      try {
        const res = await request.get('/newSimilarity/searchForCombinedSimilarity', {
          params: { word: word || '', letter: letter || '', phone: phone || '' }
        })
        if (res.code === 200) {
          const list = res.IndividualSimilarityResult || res.CombinedSimilarityResult || []
          const target = this.selectedChips.map((c) => (c.letter || '').toLowerCase()).join('')
          this.similarResultArr = list.map((item) => {
            const { chars } = markWordChars(item.word, target)
            return { ...item, chars }
          })
          if (!list.length) this.$message.info('未找到相似单词')
        }
      } catch (e) {
        // 拦截器已提示
      }
    },

    showWordDetail(index) {
      const item = this.similarResultArr[index]
      if (!item) return
      this.playWord(item.word)
      this.clearFloating()
      const fullMeaning = normalizeNewlines(item.meaning || '')
      const firstMeaning = fullMeaning ? fullMeaning.split('\n')[0].trim() : '暂无释义'
      this.floatingData = {
        word: item.word || '',
        meaning: firstMeaning || '暂无释义',
        grade: item.grade,
        term: item.term,
        unit: item.unit
      }
      this._floatingTimer = setTimeout(() => this.clearFloating(), 5000)
    },

    playWord(word) {
      if (!word) return
      const audio = new Audio(
        'https://dict.youdao.com/dictvoice?audio=' + encodeURIComponent(word) + '&type=1'
      )
      audio.play()
    },

    clearFloating() {
      if (this._floatingTimer) {
        clearTimeout(this._floatingTimer)
        this._floatingTimer = null
      }
      this.floatingData = null
    }
  }
}
</script>

<style scoped>
.row-label {
  display: inline-block;
  padding: 3px 14px;
  border-radius: 999px;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  margin-top: 12px;
}
.label-pho { background: linear-gradient(135deg, #ff7f50, #ff9f43); }
.label-let { background: linear-gradient(135deg, #c89b6c, #a67a4e); }

.chip-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 8px;
}
.chip {
  padding: 6px 14px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 700;
  color: #fff;
  cursor: pointer;
  user-select: none;
  transition: all 0.2s ease;
}
.chip-pho { background: linear-gradient(135deg, #ff6b35, #ffa502); }
.chip-let { background: linear-gradient(135deg, #b08968, #ddb892); color: #3e2a14; }
.chip-active,
.chip-selected {
  background: linear-gradient(135deg, #69db7c, #b2f2bb) !important;
  color: #1a5c2a !important;
  transform: scale(1.08);
  font-weight: 900;
}

.selected-box {
  margin-top: 16px;
  padding: 14px 16px;
  border-radius: 12px;
  background: rgba(47, 158, 68, 0.08);
  border: 1px dashed rgba(47, 158, 68, 0.3);
}
.selected-label { font-size: 13px; font-weight: 700; color: #2f9e44; }
.combo-row { display: flex; align-items: center; flex-wrap: wrap; margin-top: 8px; }
.combo-label { font-size: 13px; font-weight: 700; color: #8d5b2e; margin-right: 6px; }
.combo-sep { font-size: 15px; font-weight: 800; color: #ffa502; margin: 0 3px; }
.combo-letter { font-size: 16px; font-weight: 800; padding: 1px 4px; cursor: pointer; }
.combo-removable { color: #2f9e44; text-decoration: underline; }
.combo-locked { color: #495057; cursor: default; }
.combo-pho { font-size: 13px; font-weight: 600; color: #d9480f; }

.combo-actions { display: flex; gap: 12px; margin-top: 12px; }
.combo-btn {
  padding: 7px 22px;
  border: none;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 700;
  color: #fff;
  cursor: pointer;
}
.combo-search { background: linear-gradient(135deg, #2f9e44, #69db7c); }
.combo-clear { background: linear-gradient(135deg, #fa5252, #ffa8a8); }
</style>
